import {
  createStyles,
  makeStyles,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { ErrorStatus, SuccessStatus, WarningStatus } from '../../../../icons/StatusIcons';
import { CodecReport } from '../../../AppStateProvider/useCodecsTest/useCodecsTest';
import { toolTipContent } from '../../ToolTipsInformation/ToolTipContent';
import { InfoIcon } from '../../../../icons/InfoIcon';

const useStyles = makeStyles((theme) =>
  createStyles({
    modal: {
      width: '100%',
    },
    content: {
      padding: '3em',
      '@media screen and (max-width: 760px)': {
        padding: '0px',
      },
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },
    iconContainer: {
      display: 'flex',
      alignItems: 'stretch',
      '& svg': {
        marginRight: '0.3em',
      },
    },
    typography: {
      '@media screen and (max-width: 760px) and (min-width: 301px)': {
        width: '235px',
      },
      '@media screen and (max-width: 760px)': {
        width: '150px',
      },
    },
  })
);

interface ConnectionModalProps {
  isModalOpen: boolean;
  setIsModalOpen: (isModalOpen: boolean) => void;
  audioCodecTestStatus: boolean;
  audioCodecTestReport: CodecReport[] | null;
  videoCodecTestStatus: boolean;
  videoCodecTestReport: CodecReport[] | null;
}

let serviceStatusObj = [
  { result: 'Supported', icon: <SuccessStatus /> },
  { result: 'Particular Supported', icon: <WarningStatus /> },
  { result: 'Not Supported', icon: <ErrorStatus /> },
];

export function CodecsStatusRow({ codec, result }: { codec?: string; result: string | undefined }) {
  const classes = useStyles();
  const serviceStatus = serviceStatusObj.find((i) => i.result === result);

  return (
    <div className={classes.iconContainer}>
      {serviceStatus?.icon}
      <Typography variant="body1">
        <strong>{codec}</strong>
      </Typography>
    </div>
  );
}

export function setCodecsTestResult(codecTestReport: CodecReport[] | null) {
  if (!codecTestReport) {
    return;
  }
  var arrayContainsSupportedCodec: CodecReport[] = codecTestReport.filter((e) => e.result === 'Supported');

  if (arrayContainsSupportedCodec.length > 0 && arrayContainsSupportedCodec.length < codecTestReport?.length) {
    for (var i = 0; i < codecTestReport.length; i++) {
      if (codecTestReport[i].result === 'Not Supported') {
        arrayContainsSupportedCodec.push({ ...codecTestReport[i], result: 'Particular Supported' });
      }
    }
    return arrayContainsSupportedCodec;
  } else {
    return codecTestReport;
  }
}

export function CodecsTestModal({
  isModalOpen,
  setIsModalOpen,
  audioCodecTestStatus,
  audioCodecTestReport,
  videoCodecTestStatus,
  videoCodecTestReport,
}: ConnectionModalProps) {
  const classes = useStyles();
  var audioCodecTestReportUpdated = setCodecsTestResult(audioCodecTestReport);
  var videoCodecTestReportUpdated = setCodecsTestResult(videoCodecTestReport);
  return (
    <>
      <Dialog open={isModalOpen} onClose={() => setIsModalOpen(false)} classes={{ paper: classes.modal }}>
        <DialogTitle disableTypography>
          <Typography variant="h3" className={classes.typography}>
            Detailed Codecs support information
          </Typography>
          <IconButton aria-label="close" className={classes.closeButton} onClick={() => setIsModalOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers className={classes.content}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <Typography variant="body1">
                    <strong>Status</strong>
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body1">
                    <strong>Codec</strong>
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>
                  <div className={classes.iconContainer}>
                    <Tooltip title={toolTipContent.audioCodec} interactive leaveDelay={250}>
                      <div>
                        <InfoIcon />
                      </div>
                    </Tooltip>
                    {audioCodecTestStatus ? <SuccessStatus /> : <ErrorStatus />}
                    <Typography variant="body1">
                      <strong>Audio</strong>
                    </Typography>
                  </div>
                </TableCell>
                <TableCell>
                  <CodecsStatusRow
                    codec={'G711'}
                    result={audioCodecTestReportUpdated?.find((i) => i.codec === 'G711')?.result}
                  />
                  <CodecsStatusRow
                    codec={'G729'}
                    result={audioCodecTestReportUpdated?.find((i) => i.codec === 'G729')?.result}
                  />
                  <CodecsStatusRow
                    codec={'G722'}
                    result={audioCodecTestReportUpdated?.find((i) => i.codec === 'G722')?.result}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <div className={classes.iconContainer}>
                    <Tooltip title={toolTipContent.videoCodec} interactive leaveDelay={250}>
                      <div>
                        <InfoIcon />
                      </div>
                    </Tooltip>
                    {videoCodecTestStatus ? <SuccessStatus /> : <ErrorStatus />}
                    <Typography variant="body1">
                      <strong>Video</strong>
                    </Typography>
                  </div>
                </TableCell>
                <TableCell>
                  <CodecsStatusRow
                    codec={'H264'}
                    result={videoCodecTestReportUpdated?.find((i) => i.codec === 'H264')?.result}
                  />
                  <CodecsStatusRow
                    codec={'VP8'}
                    result={videoCodecTestReportUpdated?.find((i) => i.codec === 'VP8')?.result}
                  />
                  <CodecsStatusRow
                    codec={'VP9'}
                    result={videoCodecTestReportUpdated?.find((i) => i.codec === 'VP9')?.result}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </>
  );
}
